import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, ScanLine, Camera, AlertTriangle, CheckCircle2,
  ExternalLink, Wifi, Keyboard, Zap,
} from "lucide-react";
import { toast } from "sonner";
import BarcodeScanner from "../components/BarcodeScanner";

/**
 * Phase 87bd — Page d'aide configuration scanner Inateck BCST-47.
 *
 * Diagnostic des problèmes typiques + procédure pas-à-pas + zone de test
 * pour valider que le scanner produit le bon output.
 *
 * Le BCST-47 est un scanner 2,4 GHz dongle USB (PAS Bluetooth — c'est
 * pour ça que l'appairage BT échoue, c'est normal et attendu).
 *
 * Pour la reconfiguration, l'utilisateur doit scanner les QR de paramétrage
 * du manuel officiel Inateck (impossible à reproduire ici car proprio).
 */
export default function ScannerHelp() {
  const [testInput, setTestInput] = useState("");
  const [lastScan, setLastScan] = useState(null);
  const [lastScanType, setLastScanType] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const inputRef = useRef(null);

  const handleTestSubmit = (e) => {
    e.preventDefault();
    if (!testInput) return;
    const isValid = testInput === "TEST-MAISON-THAI-2026";
    setLastScan(testInput);
    setLastScanType(isValid ? "valid" : "wrong");
    if (isValid) toast.success("Scanner OK — Configuration correcte !");
    else toast.warning("Le code reçu ne correspond pas au QR de test — vérifiez la configuration clavier.");
    setTestInput("");
  };

  return (
    <div className="space-y-6 max-w-4xl pb-12" data-testid="scanner-help-page">
      <div>
        <Link to="/" className="text-sm text-[#1F4E3D] hover:underline flex items-center gap-1 mb-3">
          <ArrowLeft className="w-4 h-4" /> Retour au tableau de bord
        </Link>
        <div className="flex items-center gap-3">
          <ScanLine className="w-8 h-8 text-[#1F4E3D]" />
          <div>
            <h1 className="font-heading text-2xl font-medium">Aide scanner — Inateck BCST-47</h1>
            <p className="text-sm text-[#575A57]">Configuration, test et résolution des problèmes courants</p>
          </div>
        </div>
      </div>

      {/* Diagnostic */}
      <div className="bg-[#FBF4E9] border border-[#E8D3A8] rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#8C6B2F] mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-[#8C6B2F] mb-1">Diagnostic rapide</div>
            <p className="text-[#575A57] leading-relaxed">
              Le BCST-47 est un scanner <strong>2,4 GHz dongle USB</strong> (PAS Bluetooth). L&apos;appairage Bluetooth de votre tablette détecte
              parfois le dongle, mais ne peut pas s&apos;y connecter — c&apos;est <strong>normal et attendu</strong>.
              Pour fonctionner sans fil, il faut brancher le dongle USB fourni dans la tablette via un adaptateur OTG (USB-C → USB-A).
            </p>
          </div>
        </div>
      </div>

      {/* Section 1 — Symptômes */}
      <Section
        icon={Keyboard}
        title="Symptôme : 1 caractère par seconde, lettres bizarres ([ au lieu de ç, etc.)"
        color="#C05C47"
      >
        <p>
          C&apos;est le symptôme typique d&apos;un scanner configuré en <strong>clavier QWERTY US</strong> sur un système en <strong>AZERTY/QWERTZ Suisse-FR</strong>.
          Le délai par caractère vient souvent du mode <em>batch</em> ou <em>data transmission delay</em> activé par défaut.
        </p>
        <p className="mt-2">
          <strong>Solution :</strong> reconfigurer le scanner en clavier français-suisse + désactiver les délais.
          Pour cela, il faut scanner les QR de configuration du <strong>manuel officiel Inateck BCST-47</strong>.
        </p>
        <div className="mt-3 space-y-2">
          <a
            href="https://www.inateck.com/wp-content/uploads/2021/04/BCST-47-EN.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-[#1F4E3D] hover:underline font-medium"
          >
            <ExternalLink className="w-4 h-4" /> Manuel officiel Inateck BCST-47 (PDF EN)
          </a>
          <div className="text-xs text-[#575A57]">
            Procédure :
            <ol className="list-decimal ml-5 mt-1 space-y-0.5">
              <li>Scanner le QR <strong>« Restore Factory Defaults »</strong> (1<sup>re</sup> page du manuel)</li>
              <li>Scanner le QR <strong>« French / Swiss-French Keyboard »</strong> (section Keyboard Language)</li>
              <li>Scanner le QR <strong>« Inter-Character Delay : None »</strong> ou <strong>« Transmission Speed : Fast »</strong></li>
              <li>Scanner le QR <strong>« Auto-send Enter after barcode »</strong> (Terminator → CR/LF)</li>
              <li>Désactiver le mode batch : scanner <strong>« Instant Mode »</strong> (Working Mode → Instant)</li>
            </ol>
          </div>
        </div>
      </Section>

      {/* Section 2 — Sans-fil */}
      <Section
        icon={Wifi}
        title="Symptôme : ne fonctionne qu&apos;en USB filaire, pas en sans-fil"
        color="#8C6B2F"
      >
        <p>
          Le BCST-47 communique en <strong>2,4 GHz propriétaire</strong> via son dongle USB.
          Pour l&apos;utiliser sans fil sur une <strong>tablette Android</strong>, il vous faut un <strong>adaptateur OTG USB-C ↔ USB-A</strong>
          (~5 CHF chez Interdiscount/Digitec) pour brancher le dongle dans le port USB-C de la tablette.
        </p>
        <p className="mt-2">
          <strong>Sur PC Windows :</strong> le dongle se branche directement dans n&apos;importe quel port USB-A, aucun pilote requis.
        </p>
        <p className="mt-2 text-xs italic text-[#575A57]">
          ⓘ Si vous voulez du vrai Bluetooth (appairage sans dongle), le modèle <strong>Inateck BCST-60</strong> ou <strong>BCST-70</strong>
          fait le job. Le BCST-47 n&apos;est PAS un scanner Bluetooth malgré ce qu&apos;indique parfois la fiche produit.
        </p>
      </Section>

      {/* Section 3 — Caméra */}
      <Section
        icon={Camera}
        title="Solution alternative : utiliser la caméra de la tablette"
        color="#2D6A4F"
      >
        <p>
          Si le scanner USB est trop laborieux, la <strong>caméra de votre tablette Android</strong> peut faire le job, sans matériel additionnel.
          Le système supporte EAN-13, QR Code, Code128, Code39 et plus.
        </p>
        <p className="mt-2">
          <strong>Activation Firefox / Brave Android :</strong>
        </p>
        <ul className="list-disc ml-5 mt-1 text-sm space-y-0.5">
          <li><strong>Firefox</strong> : icône cadenas (barre d&apos;adresse) → Permissions → Caméra → <strong>Autoriser</strong></li>
          <li><strong>Brave</strong> : icône lion (Shields) → désactiver pour ce site → recharger → cadenas → Caméra → Autoriser</li>
          <li>Si refusé une fois : paramètres du navigateur → Sites et autorisations → Caméra → trouver maison-thai.ch et basculer</li>
        </ul>
        <Button
          onClick={() => setCameraOpen(true)}
          className="mt-3 bg-[#1F4E3D] hover:bg-[#173B2E]"
          data-testid="open-camera-test"
        >
          <Camera className="w-4 h-4 mr-2" /> Tester la caméra maintenant
        </Button>
      </Section>

      {/* Section 4 — Test de scan */}
      <Section
        icon={Zap}
        title="Test interactif : votre scanner produit-il le bon output ?"
        color="#1F4E3D"
      >
        <p className="mb-3">
          Cliquez dans le champ ci-dessous, puis scannez le QR de test à droite avec votre scanner.
          Le code attendu est <code className="bg-[#F4F4F0] px-1.5 py-0.5 rounded text-xs font-mono">TEST-MAISON-THAI-2026</code>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <form onSubmit={handleTestSubmit}>
            <Input
              ref={inputRef}
              value={testInput}
              onChange={(e) => setTestInput(e.target.value)}
              placeholder="Scannez le QR à droite…"
              className="font-mono"
              autoFocus
              data-testid="scanner-test-input"
            />
            <Button type="submit" className="mt-2" variant="outline" size="sm">
              Valider manuellement
            </Button>
            {lastScan && (
              <div
                className={`mt-3 p-3 rounded-md border ${
                  lastScanType === "valid"
                    ? "bg-[#EBF2E9] border-[#C7DDC0] text-[#2D6A4F]"
                    : "bg-[#FCEEEA] border-[#E8C1B4] text-[#C05C47]"
                }`}
                data-testid="scanner-test-result"
              >
                <div className="flex items-center gap-2 font-semibold text-sm">
                  {lastScanType === "valid" ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  {lastScanType === "valid" ? "Configuration OK !" : "Sortie inattendue"}
                </div>
                <div className="font-mono text-xs mt-1 break-all">
                  Reçu : « {lastScan} »
                </div>
                {lastScanType === "wrong" && (
                  <div className="text-xs mt-1.5 italic leading-relaxed">
                    Lettres mojibakées (« é » → « [ », « ç » → « \ ») = clavier US. Suivez la procédure ci-dessus pour configurer en
                    français-suisse.
                  </div>
                )}
              </div>
            )}
          </form>
          <div className="flex flex-col items-center justify-center p-4 bg-white border border-[#E5E5E0] rounded-lg">
            <QRCodeCanvas
              value="TEST-MAISON-THAI-2026"
              size={180}
              level="M"
              includeMargin
              data-testid="scanner-test-qr"
            />
            <p className="text-[10px] text-[#575A57] text-center mt-2 font-mono">
              TEST-MAISON-THAI-2026
            </p>
          </div>
        </div>
      </Section>

      <BarcodeScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onDetected={(code) => {
          setCameraOpen(false);
          setTestInput(code);
          handleTestSubmit({ preventDefault: () => {} });
        }}
      />
    </div>
  );
}

function Section({ icon: Icon, title, color, children }) {
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5" style={{ color }} />
        <h2 className="font-heading text-lg font-medium" style={{ color }}>
          {title}
        </h2>
      </div>
      <div className="text-sm text-[#575A57] leading-relaxed">{children}</div>
    </div>
  );
}
